import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  InputAdornment,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PageHeader, PrimaryActionButton } from '../../components/common/PageHeader';
import { employeeService } from '../../services/employee.service';
import { usePermission } from '../../hooks/usePermission';
import type { Employee } from '../../types';

export function EmployeesPage() {
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [passwordEmployee, setPasswordEmployee] = useState<Employee | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { canManageEmployees, isManager } = usePermission();
  const canManage = canManageEmployees();

  const { data, isLoading } = useQuery({
    queryKey: ['employees', page, rowsPerPage, canManage],
    queryFn: async () => {
      if (canManage) {
        return employeeService.getAll({ page: page + 1, limit: rowsPerPage });
      }
      const team = await employeeService.getMyTeam();
      return { data: team, total: team.length, page: 1, limit: team.length };
    },
  });

  const { data: managers = [] } = useQuery({
    queryKey: ['managers'],
    queryFn: () => employeeService.getManagers(),
    enabled: canManage,
  });

  const createMutation = useMutation({
    mutationFn: employeeService.create,
    onSuccess: () => {
      toast.success('Employee created. Share the email and password so they can log in.');
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setOpen(false);
      setShowPassword(false);
      reset();
    },
    onError: (err: Error) => {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.message || axiosErr.message || 'Failed to create employee');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      employeeService.setPassword(id, password),
    onSuccess: () => {
      toast.success('Password updated. Share it with the employee.');
      setPasswordEmployee(null);
      setNewPassword('');
      setShowPassword(false);
    },
    onError: (err: Error) => {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.message || axiosErr.message || 'Failed to update password');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => employeeService.delete(id),
    onSuccess: () => {
      toast.success('Employee deactivated');
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      employeeCode: '',
      department: '',
      managerId: '',
      password: '',
    },
  });

  const rows = (data?.data ?? []).filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      row.name.toLowerCase().includes(q) ||
      row.email.toLowerCase().includes(q) ||
      row.employeeCode.toLowerCase().includes(q) ||
      (row.department ?? '').toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      id: 'name',
      label: 'Employee',
      minWidth: 200,
      render: (row: Employee) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: '0.8rem',
              fontWeight: 700,
              borderRadius: '10px',
              bgcolor: 'primary.50',
              color: 'primary.dark',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            }}
          >
            {row.name.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }} noWrap>
              {row.name}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>
              {row.employeeCode}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      id: 'email',
      label: 'Contact',
      minWidth: 180,
      render: (row: Employee) => (
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }} noWrap>
            {row.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.mobile}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'department',
      label: 'Department',
      render: (row: Employee) => row.department ?? '—',
    },
    {
      id: 'manager',
      label: 'Manager',
      render: (row: Employee) => row.manager?.name ?? '—',
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: Employee) => <StatusChip status={row.status} />,
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      render: (row: Employee) => (
        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          <Tooltip title="Set password">
            <IconButton
              size="small"
              onClick={() => {
                setPasswordEmployee(row);
                setNewPassword('');
                setShowPassword(false);
              }}
              aria-label={`Set password for ${row.name}`}
            >
              <VpnKeyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
          <Tooltip title="Deactivate employee">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteId(row.id)}
              aria-label={`Deactivate ${row.name}`}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        compact
        title={isManager() ? 'My Team' : 'Employees'}
        subtitle={isManager() ? 'Set login passwords for your field employees' : 'Manage your field force team'}
        action={
          canManage ? (
            <PrimaryActionButton
              label="Add Employee"
              icon={<AddRoundedIcon />}
              onClick={() => setOpen(true)}
            />
          ) : undefined
        }
      />

      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search by name, email, or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search employees' }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.total ?? 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No employees yet"
        emptySubtext="Add your first field employee to begin managing your team."
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <form
          onSubmit={handleSubmit((d) =>
            createMutation.mutate({
              name: d.name,
              email: d.email,
              mobile: d.mobile,
              employeeCode: d.employeeCode,
              department: d.department || undefined,
              managerId: d.managerId || undefined,
              password: d.password,
            }),
          )}
        >
          <DialogTitle>Add employee</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
              <TextField
                label="Full name"
                fullWidth
                required
                autoFocus
                {...register('name', { required: true })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  {...register('email', { required: true })}
                />
                <TextField
                  label="Mobile"
                  fullWidth
                  required
                  {...register('mobile', { required: true })}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Employee code"
                  fullWidth
                  required
                  placeholder="EMP001"
                  {...register('employeeCode', { required: true })}
                />
                <TextField label="Department" fullWidth {...register('department')} />
              </Stack>
              <TextField select label="Manager" fullWidth defaultValue="" {...register('managerId')}>
                <MenuItem value="">None</MenuItem>
                {managers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Login password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                required
                autoComplete="new-password"
                helperText="Minimum 8 characters. Share this with the employee so they can log in on web or mobile."
                {...register('password', { required: true, minLength: 8 })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpen(false);
                setShowPassword(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createMutation.isPending}>
              Create employee
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!passwordEmployee}
        onClose={() => {
          setPasswordEmployee(null);
          setNewPassword('');
          setShowPassword(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 0.5 }}>
            Set a new login password for {passwordEmployee?.name}. They will use this on web and mobile.
          </Typography>
          <TextField
            label="New password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            autoFocus
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordEmployee(null);
              setNewPassword('');
              setShowPassword(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={newPassword.length < 8 || passwordMutation.isPending}
            onClick={() => {
              if (passwordEmployee) {
                passwordMutation.mutate({ id: passwordEmployee.id, password: newPassword });
              }
            }}
          >
            Save password
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        title="Deactivate employee"
        message="This will deactivate the employee account. They will no longer be able to sign in. Continue?"
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
        confirmLabel="Deactivate"
        confirmColor="error"
      />
    </Box>
  );
}
